import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import { useTheme } from '@/lib/hooks'

import { addToCart, cartTotalSelector, finalTotalSelector, removeFromCart, reset, setCurrentModal } from '../../store/checkoutSlice'
import ActionButtons from './ActionButtons'
import CartItem from './CartItem'
import CartSummary from './CartSummary'
import LoyaltyCard from './LoyaltyCard'
import { CartEmpty, CartEmptyIcon, CartHeader, CartItemCount, CartList, RightPanelWrapper } from './RightPanel.styles'

const RightPanel = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const themes = useTheme()
  const cart = useSelector(state => state.checkout.cart)
  const cartTotal = useSelector(cartTotalSelector)
  const finalTotal = useSelector(finalTotalSelector)

  const itemCount = cart.reduce((sum, item) => sum + item.qty, 0)

  // UI only: scroll the newest / changed line into view and flash it briefly
  const listRef = useRef(null)
  const prevCartRef = useRef(cart)
  const [highlight, setHighlight] = useState(null)
  const [edges, setEdges] = useState({ top: false, bottom: false })

  const listHeightRef = useRef(0)

  const updateEdges = useCallback(() => {
    const el = listRef.current
    if (!el) return
    const top = el.scrollTop > 2
    const bottom = el.scrollTop + el.clientHeight < el.scrollHeight - 2
    setEdges(prev => (prev.top === top && prev.bottom === bottom ? prev : { top, bottom }))
  }, [])

  // When the list shrinks (loyalty card / summary appears) keep the newest rows visible
  const handleListResize = useCallback(() => {
    const el = listRef.current
    if (!el) return
    if (el.clientHeight < listHeightRef.current) el.scrollTop = el.scrollHeight
    listHeightRef.current = el.clientHeight
    updateEdges()
  }, [updateEdges])

  useLayoutEffect(() => {
    const prev = prevCartRef.current
    prevCartRef.current = cart
    const changed = cart.find(item => {
      const before = prev.find(o => o.id === item.id)
      return !before || item.qty > before.qty
    })
    if (changed && listRef.current) {
      const row = listRef.current.querySelector(`[data-cart-id="${changed.id}"]`)
      if (row) row.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
      setHighlight({ id: changed.id, key: Date.now() })
    }
    updateEdges()
  }, [cart, updateEdges])

  useEffect(() => {
    if (!highlight) return undefined
    const timer = setTimeout(() => setHighlight(null), 900)
    return () => clearTimeout(timer)
  }, [highlight])

  useEffect(() => {
    // List height also changes when the loyalty card or summary appears
    const ro = new ResizeObserver(() => handleListResize())
    if (listRef.current) ro.observe(listRef.current)
    return () => ro.disconnect()
  }, [handleListResize])

  const handleIncrement = (product) => dispatch(addToCart(product))
  const handleDecrement = (product) => dispatch(removeFromCart({ id: product.id }))
  const handleRemove = (product) => dispatch(removeFromCart({ id: product.id, all: true }))
  const handlePay = () => dispatch(setCurrentModal('PAYMENT'))
  const handleCancel = () => dispatch(reset())

  return (
    <RightPanelWrapper>
      <CartHeader>
        <h2>{t('cartPanel.title')}</h2>
        {itemCount > 0 && <CartItemCount>{t('cartPanel.itemCount', { count: itemCount })}</CartItemCount>}
      </CartHeader>

      <CartList ref={listRef} onScroll={updateEdges} $fadeTop={edges.top} $fadeBottom={edges.bottom}>
        {cart.length === 0 ? (
          <CartEmpty>
            <CartEmptyIcon>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M5 8h14l-1.2 11.2a2 2 0 0 1-2 1.8H8.2a2 2 0 0 1-2-1.8L5 8z" />
                <path d="M9 10V6.5a3 3 0 0 1 6 0V10" />
              </svg>
            </CartEmptyIcon>
            <p>{t('cartPanel.empty')}</p>
          </CartEmpty>
        ) : (
          cart.map(item => (
            <CartItem
              key={item.id}
              item={item}
              highlightKey={highlight && highlight.id === item.id ? highlight.key : null}
              currency={themes.currency}
              onIncrement={() => handleIncrement(item)}
              onDecrement={() => handleDecrement(item)}
              onRemove={() => handleRemove(item)}
            />
          ))
        )}
      </CartList>

      {cart.length > 0 && (
        <>
          <LoyaltyCard />
          <CartSummary total={cartTotal} currency={themes.currency} />
          <ActionButtons
            total={finalTotal}
            currency={themes.currency}
            onPay={handlePay}
            onCancel={handleCancel}
          />
        </>
      )}
    </RightPanelWrapper>
  )
}

export default RightPanel
